import { useEffect, useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import { AppDialog } from "../../components/AppDialog";
import { AppDialogHeader } from "../../components/AppDialogHeader";
import { mediaUrl } from "../../../mediaAuth";
import { fetchSharedMediaApi } from "../../../chatApi";
import { ChatMediaViewerDialog } from "./ChatMediaViewerDialog";
import type { ChatMessage } from "../../../types";

// Was ChatSearchDialog.tsx (a Tabs-based "Shared Media" + "Messages" search
// dialog) - text search moved inline into ChatThreadScreen.tsx's header
// (Search Mode, opened from the "More" ⋮ menu), so this component now only
// ever needs to be the media grid half. Renamed rather than left as a
// single-tab shell of its old self.

function attachmentUrl(message: ChatMessage): string {
  const a = message.attachment!;
  return mediaUrl(`/api/chat/media/${a.filename}`);
}

function MediaTile({ message, onOpenImage }: { message: ChatMessage; onOpenImage: (src: string) => void }) {
  const a = message.attachment!;
  if (a.type === "image") {
    const thumbSrc = a.thumbFilename ? mediaUrl(`/api/chat/thumbs/${a.thumbFilename}`) : attachmentUrl(message);
    return (
      <button
        type="button"
        className="p-0 border-0"
        aria-label="Open photo"
        onClick={() => onOpenImage(attachmentUrl(message))}
        style={{ aspectRatio: "1 / 1", background: "var(--surface-soft)", borderRadius: 8, overflow: "hidden", cursor: "pointer" }}
      >
        <img src={thumbSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      </button>
    );
  }

  // Video/audio have no server-side thumbnail (/api/chat/thumbs 404s for
  // non-image extensions) - a labeled icon tile that opens the real file in
  // a new tab is simpler and more honest than faking a preview frame.
  return (
    <a
      href={attachmentUrl(message)}
      target="_blank"
      rel="noopener"
      aria-label={a.type === "video" ? "Open video" : "Open voice note"}
      className="d-flex align-items-center justify-content-center"
      style={{ aspectRatio: "1 / 1", background: "var(--surface-soft)", borderRadius: 8, textDecoration: "none" }}
    >
      <i className={`bi ${a.type === "video" ? "bi-camera-video-fill" : "bi-mic-fill"} text-pink`} style={{ fontSize: "1.5rem" }} aria-hidden="true" />
    </a>
  );
}

export function ChatMediaDialog({ open, onClose, conversationId }: { open: boolean; onClose: () => void; conversationId: string }) {
  const [media, setMedia] = useState<ChatMessage[] | null>(null);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [viewerSrc, setViewerSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMedia(null);
  }, [open]);

  useEffect(() => {
    if (!open || media !== null) return;
    setMediaLoading(true);
    fetchSharedMediaApi(conversationId).then((items) => {
      setMedia(items);
      setMediaLoading(false);
    });
  }, [open, media, conversationId]);

  return (
    <AppDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <AppDialogHeader icon="bi-images" title="Shared Media" onBack={onClose} />
      <DialogContent sx={{ pt: 2 }}>
        {mediaLoading ? (
          <Box className="small muted">Loading…</Box>
        ) : !media?.length ? (
          <Box className="small muted">No shared photos, videos, or voice notes yet.</Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
            {media.map((m) => (
              <MediaTile key={m.id} message={m} onOpenImage={setViewerSrc} />
            ))}
          </Box>
        )}
      </DialogContent>

      {viewerSrc && <ChatMediaViewerDialog src={viewerSrc} onClose={() => setViewerSrc(null)} />}
    </AppDialog>
  );
}
